
import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface ProfileData {
  date_of_birth: string | null;
  gender: string | null;
  phone_number: string | null;
}

export const useProfileCompletion = () => {
  const { user } = useAuth();
  const [isProfileComplete, setIsProfileComplete] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkProfileCompletion = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Get profile data from the profiles table
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('date_of_birth, gender, phone_number')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error fetching profile:', error);
          setIsProfileComplete(false);
        } else {
          // Check if required fields are provided (phone_number is now optional)
          const isComplete = !!(profile?.date_of_birth && profile?.gender);
          setIsProfileComplete(isComplete);
        }
      } catch (error) {
        console.error('Error checking profile completion:', error);
        setIsProfileComplete(false);
      } finally {
        setLoading(false);
      }
    };

    checkProfileCompletion();
  }, [user]);

  const refreshProfileStatus = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('date_of_birth, gender, phone_number')
        .eq('id', user.id)
        .single();

      if (!error && profile) {
        // Check if required fields are provided (phone_number is now optional)
        const isComplete = !!(profile.date_of_birth && profile.gender);
        setIsProfileComplete(isComplete);
      }
    } catch (error) {
      console.error('Error refreshing profile status:', error);
    } finally {
      setLoading(false);
    }
  };

  return {
    isProfileComplete,
    loading,
    refreshProfileStatus,
  };
};
